import React, { useRef, useState } from 'react';
import { Button } from '@mui/material';
import '../../styles/fonts/fonts.css';
import { useUploadProjectMutation, useUploadFirmMutation, useGetUserQuery } from 'api/api';
import FigmaModal from 'components/modal/FigmaModal';

type UploadType = 'projects' | 'firms';

interface UploadButtonProps {
    type: UploadType;
    onSuccess?: VoidFunction;
}

const UploadButton = ({ type, onSuccess }: UploadButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploadProject] = useUploadProjectMutation();
    const [uploadFirm] = useUploadFirmMutation();
    const { data: userData } = useGetUserQuery()
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Valid file types for each upload type using MIME type as keys and extensions as values
    const validFileTypes = {
        // 'application/vnd.ms-excel': '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
        // 'application/vnd.ms-excel.sheet.binary.macroEnabled.12': '.xlsb',
        'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const file = event.target.files[0];

            if (file) {
                // Checks if the file type is valid either by MIME type or extension
                const isValidType = Object.keys(validFileTypes).includes(file.type) ||
                    Object.values(validFileTypes).some(ext =>
                        file.name.toLowerCase().endsWith(ext.toLowerCase())
                    );

                if (!isValidType) {
                    alert('Please upload a valid Excel file (.xlsm or .xlsx)');
                    return;
                }

                handleUpload(file);
            }
        }
    };

    const handleUpload = async (file: File, firm?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        firm && formData.append('firm', firm);

        try {
            if (type === 'projects') {
                await uploadProject(formData).unwrap();
                if (onSuccess) {
                    onSuccess();
                }
            } else {
                await uploadFirm(formData).unwrap();
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error('Error uploading file:', error);
            alert('Failed to upload file. Please try again or contact a developer.');
        }
    }

    const handleButtonClick = () => {
        if (type === 'projects' && userData?.site_role === 'Super Admin') {
            setIsModalOpen(true);
        } else if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleModalResponse = (response: any) => {
        if (response.file && response.firm) {
            handleUpload(response.file, response.firm)
        } else if (response.file) {
            handleUpload(response.file)
        } else if (response !== 'Cancel')
            throw Error(
                "Modal response error"
            )
    }

    return (
        <>
            <FigmaModal open={isModalOpen} onClose={() => setIsModalOpen(false)} type={'uploadProject'} onResponse={handleModalResponse} />
            <div>
                <input
                    type="file"
                    accept={Object.values(validFileTypes).join(', ')}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
                <Button
                    type="button"
                    onClick={handleButtonClick}
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<img src={'/assets/icons/DownloadIcon.svg'} alt='Download Icon' />}
                >
                    {type === 'projects' ? 'Import Projects' : 'Import Firms'}
                </Button>
            </div>
        </>
    );
};

export default UploadButton;